/**
 * smoke_detected anomaly layer (L.2.2, §6.2). Uncorroborated camera smoke
 * detections awaiting operator assessment — distinct from the Response-Level
 * anomaly dots: an AMBER dot carrying a small smoke glyph (§3B.8 "needs operator
 * eyes, not yet scored"). STATIC — per the founder motion-budget revision
 * (2026-06-05, §3B.8) the only ambient map motion is live-camera dots + L3/L4
 * anomaly markers; a pending amber smoke dot never pulses. Rendered under the
 * Anomaly Queue (urgent-anomalies) layer alongside the fused hotspots + CAP.
 *
 * Imperative installer (MapView owns the viewer): requestRefresh / setVisible /
 * pick → SmokeAnomalyFeature → the smoke_detected detail panel (Confirm / Dismiss).
 */
import * as Cesium from 'cesium';
import {
  fetchSmokeDetectionAnomalies,
  type BboxQuery,
  type SmokeAnomalyFeature,
  type SmokeAnomalyFeatureCollection,
} from '../api';
import {
  SMOKE_ANOMALY_COLOR,
  SMOKE_ANOMALY_SIZE,
} from '../smokeAnomalyStyle';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { isWithinCanadaScope } from '../canadaScope';
import {
  isAcknowledged,
  bboxKey,
  ClearedGhostTracker,
  ACK_STEEL,
  ACK_STEEL_ALPHA,
  anomalyLevelBillboardColor,
} from './anomalyMarker';
import { renderDiscGlyph } from '../discGlyphComposite';

const REFRESH_DEBOUNCE_MS = 300;
const SMOKE_PREFIX = 'smokeanom:';

export interface SmokeAnomaliesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** WALL profile (#272, PART XI Ruling 1.6): lift the Law-3 acked
   *  country-zoom drop so acked-live anomalies pin ACK-STEEL at every wall
   *  zoom. Default false (operator COP — untouched). */
  getLiftAckedGate?: () => boolean;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<SmokeAnomalyFeatureCollection>;
}

export interface SmokeAnomaliesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): SmokeAnomalyFeature | null;
  destroy(): void;
}

export function installSmokeAnomaliesLayer(
  viewer: Cesium.Viewer,
  opts: SmokeAnomaliesLayerOptions = {},
): SmokeAnomaliesLayerHandle {
  const fetcher = opts.fetcher ?? fetchSmokeDetectionAnomalies;
  const ds = new Cesium.CustomDataSource('smoke-anomalies');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, SmokeAnomalyFeature>();
  const ghosts = new ClearedGhostTracker(viewer, 'smoke-anomalies');
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (fc: SmokeAnomalyFeatureCollection, viewKey: string): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    const live: Array<{ id: string; position: Cesium.Cartesian3 }> = [];
    for (const feature of fc.features) {
      const [lon, lat] = feature.geometry.coordinates;
      // Canada-scope render guard (finding 1, 2026-07-06 §5.1 — round 2 completes the round-1 guard,
      // which covered hotspots/OC-fires/CAP/flood/ice but MISSED smoke). Unacked smoke has no
      // country-zoom gate, so an ocean-coordinate shared-DB fixture would render nationally; skip
      // any smoke anomaly whose source point sits outside Canada. See canadaScope.ts.
      if (!isWithinCanadaScope(lon, lat)) continue;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      // Colour Law 3: acknowledged → ACK-STEEL static + drops from country zoom;
      // otherwise the pending "needs eyes" amber dot.
      const acked = isAcknowledged(feature.properties);
      const dotColor = acked ? ACK_STEEL : SMOKE_ANOMALY_COLOR;
      const dotAlpha = acked ? ACK_STEEL_ALPHA : 0.9;
      // PART XI Ruling 1.6 (wall only): the acked country-drop lifts so the
      // ACK-STEEL pin renders at every wall zoom.
      const ackGate =
        acked && !(opts.getLiftAckedGate?.() ?? false)
          ? new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M)
          : undefined;
      // Icon one-swing (#564, §C.1): the EXACT-ACT DISC — ONE billboard: an amber
      // "needs eyes" disc (ACK-STEEL when acked) with the white `smoke` glyph knocked
      // out. RETIRES the point-dot + white-plate CHIP. Smoke is UNSCORED (no Response
      // Level), so the fill is the pending amber, not a level colour; STATIC (§3B.8 —
      // a pending smoke dot never pulses), so billboard.color is the identity-hue
      // multiplier at the dot's alpha (no fill-breathe).
      const image = renderDiscGlyph('smoke', { fill: dotColor }) ?? undefined;
      const dot = ds.entities.add({
        id: `${SMOKE_PREFIX}${feature.id}`,
        position,
        billboard: {
          // Static (§3B.8 motion budget): size is fixed, never pulses.
          image,
          width: SMOKE_ANOMALY_SIZE,
          height: SMOKE_ANOMALY_SIZE,
          color: anomalyLevelBillboardColor(false, dotAlpha),
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: ackGate,
        },
      });
      entityToFeature.set(dot, feature);
      live.push({ id: feature.id, position });
    }
    // Law 3 cleared-ghost: markers that departed on this same view fade to a grey
    // ghost for ~60s, then auto-leave.
    ghosts.reconcile(live, viewKey);
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc, bboxKey(bbox));
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchSmokeDetectionAnomalies failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
      ghosts.setVisible(visible);
    },
    pick(entity: unknown): SmokeAnomalyFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      ghosts.destroy();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
